import { byId, isAr, trackEvent } from './calc-common.js';
import { copyText } from './extra-common.js';

// Mapping tables for Arabic 101 <-> US QWERTY
const EN_TO_AR = {
  '`': 'ذ', '~': 'ّ',
  'q': 'ض', 'Q': 'َ',
  'w': 'ص', 'W': 'ً',
  'e': 'ث', 'E': 'ُ',
  'r': 'ق', 'R': 'ٌ',
  't': 'ف', 'T': 'لإ',
  'y': 'غ', 'Y': 'إ',
  'u': 'ع', 'U': '‘',
  'i': 'ه', 'I': '÷',
  'o': 'خ', 'O': '×',
  'p': 'ح', 'P': '؛',
  '[': 'ج', '{': '<',
  ']': 'د', '}': '>',
  'a': 'ش', 'A': 'ِ',
  's': 'س', 'S': 'ٍ',
  'd': 'ي', 'D': ']',
  'f': 'ب', 'F': '[',
  'g': 'ل', 'G': 'لأ',
  'h': 'ا', 'H': 'أ',
  'j': 'ت', 'J': 'ـ',
  'k': 'ن', 'K': '،',
  'l': 'م', 'L': '/',
  ';': 'ك', ':': ':',
  '\'': 'ط', '"': '"',
  'z': 'ئ', 'Z': '~',
  'x': 'ء', 'X': 'ْ',
  'c': 'ؤ', 'C': '}',
  'v': 'ر', 'V': '{',
  'b': 'لا', 'B': 'لآ',
  'n': 'ى', 'N': 'آ',
  'm': 'ة', 'M': '’',
  ',': 'و', '<': ',',
  '.': 'ز', '>': '.',
  '/': 'ظ', '?': '؟'
};

// Build inverted map AR_TO_EN
const AR_TO_EN = {};
for (const [enKey, arKey] of Object.entries(EN_TO_AR)) {
  AR_TO_EN[arKey] = enKey;
}

// Special ligature edge cases
AR_TO_EN['لأ'] = 'G';
AR_TO_EN['لإ'] = 'T';
AR_TO_EN['لآ'] = 'B';
AR_TO_EN['لا'] = 'b';

let currentMode = 'auto'; // 'auto' | 'en2ar' | 'ar2en'

export function detectDirection(text) {
  if (!text || !text.trim()) return 'en2ar';
  let arCount = 0;
  let enCount = 0;

  for (const ch of text) {
    if (/[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/.test(ch)) {
      arCount++;
    } else if (/[a-zA-Z]/.test(ch)) {
      enCount++;
    }
  }

  return arCount > enCount ? 'ar2en' : 'en2ar';
}

export function convertText(text, direction) {
  if (!text) return { result: '', detected: 'en2ar' };
  const dir = direction === 'auto' ? detectDirection(text) : direction;
  const map = dir === 'en2ar' ? EN_TO_AR : AR_TO_EN;

  let result = '';
  let i = 0;
  while (i < text.length) {
    // Check 2-char ligature first if converting from Arabic
    if (dir === 'ar2en' && i + 1 < text.length) {
      const twoChar = text.slice(i, i + 2);
      if (map[twoChar]) {
        result += map[twoChar];
        i += 2;
        continue;
      }
    }
    const char = text[i];
    result += map[char] !== undefined ? map[char] : char;
    i++;
  }
  return { result, detected: dir };
}

export function initKeyboardLayoutConverter() {
  const container = byId('keyboard-layout-converter-app');
  if (!container) return;

  const sourceInput = byId('klc-source-text');
  const resultOutput = byId('klc-result-text');
  const sourceCount = byId('klc-source-count');
  const resultCount = byId('klc-result-count');
  const detectedText = byId('klc-detected-text');
  const modeButtons = container.querySelectorAll('.klc-segment-btn');
  const swapBtn = byId('klc-swap-btn');
  const clearBtn = byId('klc-clear-btn');
  const copyBtn = byId('klc-copy-btn');
  const pasteBtn = byId('klc-paste-btn');
  const useAsInputBtn = byId('klc-use-as-input-btn');
  const sampleChips = container.querySelectorAll('.klc-sample-chip');

  let hasStarted = false;
  let completionTimer = null;

  const updateConversion = () => {
    const text = sourceInput?.value || '';
    if (text.trim() && !hasStarted) {
      hasStarted = true;
      trackEvent('tool_started', 'keyboard-layout-converter');
    }

    const { result, detected } = convertText(text, currentMode);

    if (resultOutput) {
      resultOutput.value = result;
    }

    if (text.trim()) {
      if (completionTimer) clearTimeout(completionTimer);
      completionTimer = setTimeout(() => {
        trackEvent('tool_completed', 'keyboard-layout-converter', { chars: text.length });
      }, 700);
    }

    // Counters
    const inWords = text.trim() ? text.trim().split(/\s+/).length : 0;
    const outWords = result.trim() ? result.trim().split(/\s+/).length : 0;

    if (sourceCount) {
      sourceCount.textContent = `${text.length} ${isAr ? 'حرف' : 'chars'} · ${inWords} ${isAr ? 'كلمة' : 'words'}`;
    }
    if (resultCount) {
      resultCount.textContent = `${result.length} ${isAr ? 'حرف' : 'chars'} · ${outWords} ${isAr ? 'كلمة' : 'words'}`;
    }

    // Smart status badge
    if (detectedText) {
      if (!text.trim()) {
        detectedText.textContent = isAr ? 'جاهز للتحويل التلقائي فوراً' : 'Ready for instant auto-conversion';
      } else if (currentMode === 'auto') {
        if (detected === 'en2ar') {
          detectedText.textContent = isAr ? 'كشف ذكي: إنجليزي ➔ عربي' : 'Auto-detected: English ➔ Arabic';
        } else {
          detectedText.textContent = isAr ? 'كشف ذكي: عربي ➔ إنجليزي' : 'Auto-detected: Arabic ➔ English';
        }
      } else if (currentMode === 'en2ar') {
        detectedText.textContent = isAr ? 'وضع محدد: إنجليزي ➔ عربي' : 'Selected: English ➔ Arabic';
      } else {
        detectedText.textContent = isAr ? 'وضع محدد: عربي ➔ إنجليزي' : 'Selected: Arabic ➔ English';
      }
    }
  };

  // Real-time automatic conversion on typing and editing
  if (sourceInput) {
    sourceInput.addEventListener('input', updateConversion);
    sourceInput.addEventListener('keyup', updateConversion);
    sourceInput.addEventListener('paste', () => setTimeout(updateConversion, 10));
    sourceInput.addEventListener('change', updateConversion);
  }

  // Smart segmented mode switcher
  modeButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      modeButtons.forEach(b => {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');
      currentMode = btn.dataset.value || 'auto';
      updateConversion();
      trackEvent('tool_interacted', 'keyboard-layout-converter', { mode: currentMode });
    });
  });

  // Swap direction button
  if (swapBtn) {
    swapBtn.addEventListener('click', () => {
      if (currentMode === 'en2ar') {
        currentMode = 'ar2en';
      } else if (currentMode === 'ar2en') {
        currentMode = 'en2ar';
      } else {
        const detected = detectDirection(sourceInput?.value || '');
        currentMode = detected === 'en2ar' ? 'ar2en' : 'en2ar';
      }
      modeButtons.forEach(b => {
        const isActive = b.dataset.value === currentMode;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      updateConversion();
    });
  }

  // Clear button
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      if (sourceInput) sourceInput.value = '';
      if (resultOutput) resultOutput.value = '';
      updateConversion();
      sourceInput?.focus();
    });
  }

  // Paste from clipboard button
  if (pasteBtn && sourceInput) {
    pasteBtn.addEventListener('click', async () => {
      try {
        if (navigator.clipboard?.readText) {
          const clipText = await navigator.clipboard.readText();
          if (clipText) {
            sourceInput.value = clipText;
            updateConversion();
          }
        } else {
          sourceInput.focus();
        }
      } catch {
        sourceInput.focus();
      }
    });
  }

  // Move result to input button
  if (useAsInputBtn && sourceInput && resultOutput) {
    useAsInputBtn.addEventListener('click', () => {
      const res = resultOutput.value;
      if (!res) return;
      sourceInput.value = res;
      updateConversion();
      sourceInput.focus();
    });
  }

  // Copy button
  if (copyBtn && resultOutput) {
    const originalHtml = copyBtn.innerHTML;
    copyBtn.addEventListener('click', async () => {
      const text = resultOutput.value;
      if (!text) return;
      await copyText(text);
      copyBtn.classList.add('copied');
      copyBtn.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:14px; height:14px;">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        <span>${isAr ? 'تم النسخ!' : 'Copied!'}</span>
      `;
      setTimeout(() => {
        copyBtn.classList.remove('copied');
        copyBtn.innerHTML = originalHtml;
      }, 2000);
    });
  }

  // Quick sample chips
  sampleChips.forEach(chip => {
    chip.addEventListener('click', () => {
      const sample = chip.dataset.sample;
      if (sourceInput && sample) {
        sourceInput.value = sample;
        updateConversion();
      }
    });
  });

  // Initialize
  updateConversion();
  trackEvent('tool_started', 'keyboard-layout-converter');
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKeyboardLayoutConverter);
  } else {
    initKeyboardLayoutConverter();
  }
}
